// src/pages/ReAssignTaskList.jsx

import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect , useMemo  } from 'react';
import axios from 'axios';
import { Box, Grid, Paper, Typography, Button, Snackbar, Alert, Chip, Stack, Checkbox, CircularProgress, Card, CardContent, useTheme, InputAdornment, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Dropdown from '../components/Dropdown';
import { useAuth } from '../context/AuthContext';
import NoDataComponent from '../components/NoDataComponent';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import CenterLoader from '../components/CenterLoader';
import { useMediaQuery } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';


const ReAssignTaskList = ({ connectionString }) => {
    const path = connectionString;
    const { user, selectedCompany } = useAuth();
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // const [selectAll, setSelectAll] = useState(false);

    // Decide which roles can reassign tasks:
    const allowedRoles = ['Team Leader', 'Admin'];
    const [totalCount, setTotalCount] = useState(0);

    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');

    const [usersOptions, setUsersOptions] = useState([]);
    const [filters, setFilters] = useState({
        assign: '',
        assignName: '',
        reAssign: '',
        reAssignName: '',
    });

    const filteredMobileRows = useMemo(() => {
    if (!isMobile) return rows;
    if (!mobileSearchQuery.trim()) return rows;

    const q = mobileSearchQuery.toLowerCase();

    return rows.filter((row) => {
        const title = row.TaskTitle?.toLowerCase() || '';

        return (
            title.startsWith(q) ||                               // 🔥 primary
            // row.Description?.toLowerCase().includes(q) ||
            // row.AssignedToName?.toLowerCase().includes(q) ||
            row.Status?.toLowerCase().includes(q) ||
            row.Priority?.toLowerCase().includes(q)
        );
    });
}, [rows, mobileSearchQuery, isMobile]);


    useEffect(() => {
        axios
            .get(`${path}/api/users`, { params: { companyId } })
            .then((res) => setUsersOptions(res.data.data || []));
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const fetchData = async () => {
        if (!allowedRoles.includes(role)) {
            setSnackbar({
                open: true,
                message: 'Access denied: Insufficient permissions to fetch tasks.',
                severity: 'error',
            });
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const res = await axios.get(`${path}/api/tasks/reassign-list`, {
                params: {
                    userId,
                    companyId,
                    role,
                },
                headers: {
                    'Cache-Control': 'no-store',
                },
            });
            setRows(res.data.data || []);
            setSelectionModel([]);
            setSelectedUserId(null);
        } catch {
            setSnackbar({ open: true, message: 'Failed to fetch tasks.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!filters.reAssign || selectionModel.length === 0) {
            return setSnackbar({
                open: true,
                message: 'Please select tasks and choose Re-Assign user.',
                severity: 'warning',
            });
        }

        const selectedRows = getSelectedRows(selectionModel);
        const uniqueAssignUsers = Array.from(new Set(selectedRows.map((r) => r.AssignedToId)));

        if (uniqueAssignUsers.length > 1) {
            return setSnackbar({
                open: true,
                message: 'Only tasks from the same assignee can be reassigned together.',
                severity: 'error',
            });
        }

        if (filters.assign === filters.reAssign) {
            return setSnackbar({
                open: true,
                message: 'Assign and Re-Assign users must be different.',
                severity: 'error',
            });
        }

        const payload = {
            assignId: filters.assign,
            assign: filters.assignName,
            reAssignId: filters.reAssign,
            reAssign: filters.reAssignName,
            taskIds: selectionModel,
            companyId,
            changedById: userId,
        };

        try {
            await axios.post(`${path}/api/tasks/reassign`, payload);
            setSnackbar({ open: true, message: 'Tasks reassigned successfully!', severity: 'success' });
            handleReset();
            fetchData();
        } catch {
            setSnackbar({ open: true, message: 'Reassignment failed.', severity: 'error' });
        }
    };

    const handleReset = () => {
        setFilters({ assign: '', assignName: '', reAssign: '', reAssignName: '' });
        setSelectionModel([]);
        setSelectedUserId(null);
        fetchData();
    };

    const getSelectedRows = (ids) => rows.filter((row) => ids.includes(row.TaskId));

    const handleCheckboxChange = (rowId) => {
        const selectedIds = [...selectionModel];
        const isSelected = selectedIds.includes(rowId);

        if (isSelected) {
            const newSelection = selectedIds.filter((id) => id !== rowId);
            setSelectionModel(newSelection);

            if (newSelection.length === 0) {
                setFilters((prev) => ({ ...prev, assign: '', assignName: '' }));
                setSelectedUserId(null);
            }
        } else {
            const row = rows.find((r) => r.TaskId === rowId);
            if (!row) return;

            if (selectedIds.length > 0 && row.AssignedToId !== selectedUserId) {
                setSnackbar({ open: true, message: 'Assignee does not match existing selection.', severity: 'warning' });
                return;
            }

            setSelectionModel([...selectedIds, rowId]);
            setFilters((prev) => ({
                ...prev,
                assign: row.AssignedToId,
                assignName: row.AssignedToName,
            }));
            if (selectedIds.length === 0) {
                setSelectedUserId(row.AssignedToId);
            }
        }
    };

    const handleHeaderCheckboxChange = () => {
        if (selectionModel.length > 0) {
            setSelectionModel([]);
            setSelectedUserId(null);
            setFilters((prev) => ({ ...prev, assign: '', assignName: '' }));
        } else if (rows.length > 0) {
            const firstRow = rows[0];
            const matchingRows = rows.filter((row) => row.AssignedToId === firstRow.AssignedToId);
            const newSelection = matchingRows.map((row) => row.TaskId);
            setSelectionModel(newSelection);
            setSelectedUserId(firstRow.AssignedToId);
            setFilters((prev) => ({
                ...prev,
                assign: firstRow.AssignedToId,
                assignName: firstRow.AssignedToName,
            }));
        }
    };

    const getMobileHeaderCheckboxProps = () => {
        if (rows.length === 0 || selectedUserId === null) {
            return { checked: false, indeterminate: false, disabled: true };
        }

        const matchingRows = rows.filter(
            (row) => row.AssignedToId === selectedUserId
        );

        const matchingIds = matchingRows.map((r) => r.TaskId);

        const allSelected =
            matchingIds.length > 0 &&
            matchingIds.every((id) => selectionModel.includes(id));

        const someSelected =
            matchingIds.some((id) => selectionModel.includes(id)) && !allSelected;

        return {
            checked: allSelected,
            indeterminate: someSelected,
            disabled: false,
        };
    };


    const getHeaderCheckboxProps = () => {
        if (rows.length === 0) return { checked: false, indeterminate: false, disabled: true };

        const matchingRows =
            selectedUserId !== null ? rows.filter((row) => row.AssignedToId === selectedUserId) : [];
        const matchingRowIds = matchingRows.map((row) => row.TaskId);

        const allSelected =
            matchingRows.length > 0 && matchingRowIds.every((id) => selectionModel.includes(id));
        const someSelected =
            selectionModel.length > 0 &&
            matchingRowIds.some((id) => selectionModel.includes(id)) &&
            !allSelected;

        return {
            checked: allSelected,
            indeterminate: someSelected,
            disabled: rows.length === 0,
        };
    };

    const columns = [
        // ✅ Keep your custom controlled checkbox column
        {
            field: 'selection',
            headerName: '',
            width: 70,
            sortable: false,
            filterable: false,
            renderHeader: () => (
                <Checkbox
                    {...getHeaderCheckboxProps()}
                    onChange={handleHeaderCheckboxChange}
                    color="primary"
                />
            ),
            renderCell: (params) => (
                <Checkbox
                    checked={selectionModel.includes(params.row.TaskId)}
                    onChange={() => handleCheckboxChange(params.row.TaskId)}
                    color="primary"
                />
            ),
        },

        // ✅ Task – same visual as TaskList (title + description + created date)
        {
            field: 'TaskTitle',
            headerName: 'Task',
            flex: 1.5,
            renderCell: (params) => {
                const row = params.row;
                const createdDate = row.CreatedAt
                    ? new Date(row.CreatedAt).toLocaleString('en-GB')
                    : '-';

                return (
                    <Box
                        sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            my: 1,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {row.TaskTitle || '-'}
                        </Typography>

                        {row.Description && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {row.Description}
                            </Typography>
                        )}

                        <Typography variant="caption" color="text.secondary">
                            Created: {createdDate}
                        </Typography>
                    </Box>
                );
            },
        },

        // ✅ Assigned To – same as TaskList (show “By: AssignedByName”)
        {
            field: 'AssignedToName',
            headerName: 'Assigned To',
            flex: 0.7,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>
                        {params.value || '—'}
                    </Typography>
                    {params.row.AssignedByName && (
                        <Typography variant="caption" color="text.secondary">
                            By: {params.row.AssignedByName}
                        </Typography>
                    )}
                </Box>
            ),
        },

        // ✅ DueDateTime – copied from TaskList
        {
            field: 'DueDateTime',
            headerName: 'Due Date & Time',
            filterable: false,
            flex: 0.9,
            renderCell: (params) => {
                const due = params.value
                    ? new Date(params.value).toLocaleString('en-GB')
                    : '—';
                return (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {due}
                    </Typography>
                );
            },
        },

        // ✅ Priority – same logic as before (this already matched TaskList)
        {
            field: 'Priority',
            headerName: 'Priority',
            flex: 0.4,
            renderCell: (params) => {
                const priority = (params.value || '').toLowerCase();
                let color = '#43a047';
                if (priority === 'high') color = '#e53935';
                else if (priority === 'mid' || priority === 'medium') color = '#fbc02d';
                return (
                    <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
                        {params.value || '—'}
                    </Typography>
                );
            },
        },

        // ✅ Status – same as TaskList
        {
            field: 'Status',
            headerName: 'Status',
            flex: 0.5,
            renderCell: (params) => {
                const status = (params.value || '').toLowerCase();
                let chipStyles = {};
                if (status === 'completed') chipStyles = { bgcolor: 'success.main', color: themeColors.neutral.surface };
                else if (status === 'cancelled') chipStyles = { bgcolor: themeColors.error.dark, color: themeColors.neutral.surface };
                else chipStyles = { bgcolor: '#FF4C00', color: themeColors.neutral.surface };

                return (
                    <Chip
                        label={params.value || '—'}
                        size="small"
                        sx={{ fontWeight: 600, ...chipStyles }}
                    />
                );
            },
        },
    ];

   const toggleSelectAllMobile = () => {
    if (selectionModel.length > 0) {
        setSelectionModel([]);
        setSelectedUserId(null);
        setFilters((prev) => ({ ...prev, assign: '', assignName: '' }));
        return;
    }

    if (filteredMobileRows.length > 0) {
        const firstRow = filteredMobileRows[0];
        const matchingRows = filteredMobileRows.filter(
            (row) => row.AssignedToId === firstRow.AssignedToId
        );

        setSelectionModel(matchingRows.map((r) => r.TaskId));
        setSelectedUserId(firstRow.AssignedToId);
        setFilters((prev) => ({
            ...prev,
            assign: firstRow.AssignedToId,
            assignName: firstRow.AssignedToName,
        }));
    }
};



    const renderMobileCards = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (filteredMobileRows.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CustomNoDataOverlay />
                </Box>
            );
        }
        

        if (rows.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CustomNoDataOverlay />
                    <Typography sx={{ mt: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
                        Page: {paginationModel.page + 1} | Total: {totalCount}
                    </Typography>
                </Box>
            );
        }



        return (
            <>
                {filteredMobileRows.map((row) => {
                    const isSelected = selectionModel.includes(row.TaskId);
                    let color = '#43a047'; // low
                    if (row.Priority === 'high') color = '#e53935';
                    else if (row.Priority === 'mid' || row.Priority === 'medium') color = '#fbc02d';

                    let chipStyles = {}
                    if (row.Status === 'completed') chipStyles = { bgcolor: 'success.main', color: themeColors.neutral.surface };
                    else if (row.Status === 'cancelled') chipStyles = { bgcolor: themeColors.error.dark, color: themeColors.neutral.surface };
                    else chipStyles = { bgcolor: '#FF4C00', color: themeColors.neutral.surface };

                    return (
                        <Card
                            key={row.TaskId}
                            sx={{
                                mb: 1.5,
                                boxShadow: 3,
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: '0.2s',
                                '&:hover': { boxShadow: 5 },
                                borderLeft: '4px solid',
                                borderLeftColor: 'primary.main',
                                cursor: 'pointer'
                            }}
                        >
                            <CardContent sx={{ py: 1.8, px: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2
                                    }}
                                >
                                    {/* LEFT SIDE */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                                        {role !== 'User' &&
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => handleCheckboxChange(row.TaskId)}
                                                // onChange={() =>
                                                //     setSelectionModel((prev) => {
                                                //         const updated = isSelected
                                                //             ? prev.filter((id) => id !== row.TaskId)
                                                //             : [...prev, row.TaskId]
                                                //         selectedIdsRef.current = updated; // 🔥 freeze selection
                                                //         return updated;
                                                //     })
                                                // }
                                                sx={{ mt: 0.4 }}
                                            />}

                                        <Box>
                                            {/* Task Title */}
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                                sx={{ textDecoration: 'underline' }}
                                            >
                                                {row.TaskTitle || '—'}
                                            </Typography>

                                            {/* Assigned Info */}
                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.4 }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    To: {row.AssignedToName || '—'}
                                                </Typography>

                                                {row.AssignedByName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        By: {row.AssignedByName}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Dates */}
                                            <Box sx={{ display: 'flex', flexDirection: "column", mt: 0.4 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Created: {row.CreatedAt}
                                                </Typography>

                                                <Typography variant="caption" color="text.secondary">
                                                    Due:{' '}
                                                    {row.DueDateTime
                                                        ? new Date(row.DueDateTime).toLocaleString('en-GB')
                                                        : '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* RIGHT SIDE – Priority + Status */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexDirection: "column",
                                            gap: 1,
                                            mt: 0.5
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Priority:{" "}<span style={{ color, fontWeight: 600 }}>  {row.Priority || '—'}</span>
                                        </Typography>

                                        <Typography variant="body2">
                                            Status:{" "}
                                            <Chip
                                                label={row.Status || '—'}
                                                size="small"
                                                sx={{ fontWeight: 600, ...chipStyles }}
                                            />
                                        </Typography>

                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                    );
                })}

                {/* Pagination */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        py: 2
                    }}
                >
                    <Button
                        variant="outlined"
                        disabled={paginationModel.page === 0}
                        onClick={() =>
                            setPaginationModel((p) => ({
                                ...p,
                                page: Math.max(0, p.page - 1)
                            }))
                        }
                    >
                        ← Previous
                    </Button>

                    <Typography variant="body2">
                        Page {paginationModel.page + 1} of{' '}
                        {Math.ceil(totalCount / paginationModel.pageSize) || 1}
                    </Typography>

                    <Button
                        variant="outlined"
                        disabled={
                            (paginationModel.page + 1) * paginationModel.pageSize >= totalCount
                        }
                        onClick={() =>
                            setPaginationModel((p) => ({
                                ...p,
                                page: p.page + 1
                            }))
                        }
                    >
                        Next →
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <Box sx={{ pt: 1, pr: 1, borderRadius: 2, ml: 2 }}>
            {!allowedRoles.includes(role) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <Typography variant="h5" fontWeight={600} color="error">
                        403 Unauthorized Access
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                        🔁 Re-Assign Task List
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Dropdown
                                label="Re-Assign To"
                                value={filters.reAssign}
                                options={usersOptions}
                                valueKey="UserId"
                                labelKey="employeeName"
                                onChange={(selected) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        reAssign: selected.UserId,
                                        reAssignName: selected.employeeName,
                                    }))
                                }
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button variant="contained" color="primary" onClick={handleSubmit}>
                                    Submit
                                </Button>
                                <Button variant="outlined" onClick={handleReset}>
                                    Reset
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    <Paper elevation={2} sx={{ bgcolor: themeColors.neutral.surface, borderRadius: 2 }}>
                        <Box sx={{ height: '72vh', maxWidth: '100%', overflow: 'auto' }}>
                            {
                                isMobile ? <>
                                    <Box sx={{ mt: 1, mb: 2, px: 1 }}>
                                        <TextField fullWidth size="small" placeholder="Search Tasks..." value={mobileSearchQuery} onChange={(e) => setMobileSearchQuery(e.target.value)} variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    {role !== 'User' && selectionModel.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }} >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    {...getMobileHeaderCheckboxProps()}
                                                    onChange={toggleSelectAllMobile}
                                                    size="small"
                                                />

                                                <Typography variant="body2" sx={{ ml: 1 }}>
                                                    {selectionModel.length} selected
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ width: '100%', overflowX: 'hidden', px: 1 }}>
                                        {renderMobileCards()}
                                    </Box>
                                </>
                                    : (loading ? <CenterLoader /> :
                                        rows?.length === 0 ? <NoDataComponent /> :
                                            <DataGrid
                                                // autoHeight
                                                getRowHeight={() => 'auto'}
                                                rows={rows}
                                                columns={columns}
                                                getRowId={(r) => r.TaskId}
                                                loading={loading}
                                                pagination
                                                rowsPerPageOptions={[50, 100, 150]}
                                                sx={{
                                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.neutral.gridHeader, borderBottom: `1px solid ${themeColors.neutral.border}` },
                                                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                    '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                                    '& .MuiDataGrid-filler': { backgroundColor: themeColors.neutral.surface },
                                                    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: themeColors.neutral.gridHeader },
                                                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                                    '& .MuiDataGrid-row:hover': { backgroundColor: themeColors.primary.tint },
                                                    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${themeColors.primary.selected} !important` },
                                                    '& .MuiDataGrid-cell': {
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    },
                                                }}
                                                slots={{
                                                    noResultsOverlay: CustomNoDataOverlay
                                                }}
                                            />)
                            }

                        </Box>
                    </Paper>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                           anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} variant="filled">
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </>
            )}
        </Box>
    );
};

export default ReAssignTaskList;
